import React, { useEffect, useRef, useMemo, useCallback } from "react";
import { StormcloudVideoPlayer } from "../player/StormcloudVideoPlayer";
import type { StormcloudVideoPlayerConfig } from "../types";
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaVolumeDown,
} from "react-icons/fa";

const ExpandIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M9 4H4v5" />
    <path d="M20 9V4h-5" />
    <path d="M15 20h5v-5" />
    <path d="M4 15v5h5" />
  </svg>
);

const CompressIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 9h5V4" />
    <path d="M15 4v5h5" />
    <path d="M20 15h-5v5" />
    <path d="M9 20v-5H4" />
  </svg>
);

export type StormcloudVideoPlayerProps = Omit<
  StormcloudVideoPlayerConfig,
  "videoElement"
> &
  React.VideoHTMLAttributes<HTMLVideoElement> & {
    onReady?: (player: StormcloudVideoPlayer) => void;
    wrapperClassName?: string;
    wrapperStyle?: React.CSSProperties;
    licenseKey?: string;
  };

const CRITICAL_PROPS = [
  "src",
  "allowNativeHls",
  "isLiveStream",
  "licenseKey",
  "isVmap",
  "vmapUrl",
  "lowLatencyMode",
  "driftToleranceMs",
  "vastMode",
  "ctvAdRequest",
] as const;

const CONTROLS_HIDE_DELAY = 3000;
const DEFAULT_PLAYER_ASPECT_RATIO = 16 / 9;

export const StormcloudVideoPlayerComponent: React.FC<StormcloudVideoPlayerProps> =
  React.memo(
    (props) => {
      const {
        src,
        autoplay,
        muted,
        lowLatencyMode,
        allowNativeHls,
        isLiveStream,
        driftToleranceMs,
        immediateManifestAds,
        debugAdTiming,
        showCustomControls,
        hideLoadingIndicator,
        onVolumeToggle,
        onFullscreenToggle,
        onControlClick,
        onReady,
        wrapperClassName,
        wrapperStyle,
        className,
        style,
        controls,
        playsInline,
        preload,
        poster,
        children,
        licenseKey,
        vastMode,
        vastTagUrl,
        isVmap,
        vmapUrl,
        minSegmentsBeforePlay,
        ctvAdRequest,
        ...restVideoAttrs
      } = props;

      const videoRef = useRef<HTMLVideoElement | null>(null);
      const playerRef = useRef<StormcloudVideoPlayer | null>(null);
      const bufferingTimeoutRef = useRef<number | null>(null);
      const controlsTimerRef = useRef<number | null>(null);
      const wrapperRef = useRef<HTMLDivElement | null>(null);
      const [adStatus, setAdStatus] = React.useState<{
        showAds: boolean;
        currentIndex: number;
        totalAds: number;
        remainingSeconds: number;
      }>({ showAds: false, currentIndex: 0, totalAds: 0, remainingSeconds: 0 });

      const [shouldShowNativeControls, setShouldShowNativeControls] =
        React.useState(true);

      const [isMuted, setIsMuted] = React.useState(false);
      const [isFullscreen, setIsFullscreen] = React.useState(false);
      const [isPlaying, setIsPlaying] = React.useState(false);
      const [currentTime, setCurrentTime] = React.useState(0);
      const [duration, setDuration] = React.useState(0);
      const [volume, setVolume] = React.useState(1);
      const [playbackRate, setPlaybackRate] = React.useState(1);
      const [showVolumeSlider, setShowVolumeSlider] = React.useState(false);
      const [showSpeedMenu, setShowSpeedMenu] = React.useState(false);
      const [isLoading, setIsLoading] = React.useState(true);
      const [isBuffering, setIsBuffering] = React.useState(false);
      const [showCenterPlay, setShowCenterPlay] = React.useState(false);
      const [showLicenseWarning, setShowLicenseWarning] = React.useState(false);
      const [controlsVisible, setControlsVisible] = React.useState(true);
      const [viewportWidth, setViewportWidth] = React.useState(
        typeof window !== "undefined" ? window.innerWidth : 1920
      );
      const [isPortrait, setIsPortrait] = React.useState(
        typeof window !== "undefined"
          ? window.innerHeight > window.innerWidth
          : false
      );
      const [playerAspectRatio, setPlayerAspectRatio] = React.useState(
        DEFAULT_PLAYER_ASPECT_RATIO
      );

      const getResponsiveScale = () => {
        if (viewportWidth < 480) return 0.7;
        if (viewportWidth < 768) return 0.8;
        if (viewportWidth < 1024) return 0.9;
        return 1;
      };

      const responsiveScale = getResponsiveScale();

      const resetControlsTimer = useCallback(() => {
        if (controlsTimerRef.current) {
          clearTimeout(controlsTimerRef.current);
        }
        setControlsVisible(true);
        controlsTimerRef.current = window.setTimeout(() => {
          if (!showVolumeSlider && !showSpeedMenu) {
            setControlsVisible(false);
          }
        }, CONTROLS_HIDE_DELAY);
      }, [showVolumeSlider, showSpeedMenu]);

      const handleWrapperMouseMove = useCallback(() => {
        resetControlsTimer();
      }, [resetControlsTimer]);

      const handleWrapperMouseLeave = useCallback(() => {
        if (!showVolumeSlider && !showSpeedMenu) {
          setControlsVisible(false);
        }
      }, [showVolumeSlider, showSpeedMenu]);

      const formatTime = (seconds: number): string => {
        if (!isFinite(seconds)) return "0:00:00";
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${hours}:${minutes
          .toString()
          .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
      };

      const handlePlayPause = () => {
        if (videoRef.current) {
          if (videoRef.current.paused) {
            const hasValidSource =
              videoRef.current.src ||
              (videoRef.current.currentSrc &&
                videoRef.current.currentSrc !== "") ||
              videoRef.current.readyState >= 1;

            if (hasValidSource) {
              videoRef.current.play()?.catch((error) => {
                console.error("[StormcloudVideoPlayer] Failed to play:", error);
              });
              setShowCenterPlay(false);
            } else {
              console.warn(
                "[StormcloudVideoPlayer] Cannot play: video has no valid source"
              );
            }
          } else {
            videoRef.current.pause();
            setShowCenterPlay(true);
          }
        }
        resetControlsTimer();
      };

      const handleCenterPlayClick = () => {
        if (videoRef.current && videoRef.current.paused) {
          const hasValidSource =
            videoRef.current.src ||
            (videoRef.current.currentSrc &&
              videoRef.current.currentSrc !== "") ||
            videoRef.current.readyState >= 1;

          if (hasValidSource) {
            videoRef.current.play()?.catch((error) => {
              console.error("[StormcloudVideoPlayer] Failed to play:", error);
            });
            setShowCenterPlay(false);
          } else {
            console.warn(
              "[StormcloudVideoPlayer] Cannot play: video has no valid source"
            );
          }
        }
      };

      const handleTimelineSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        if (videoRef.current && duration > 0 && isFinite(duration)) {
          const rect = e.currentTarget.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const progress = Math.max(0, Math.min(1, clickX / rect.width));
          const newTime = progress * duration;

          if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
            videoRef.current.currentTime = newTime;
          }
        }
        resetControlsTimer();
      };

      const handleVolumeChange = (newVolume: number) => {
        if (playerRef.current && isFinite(newVolume)) {
          const clampedVolume = Math.max(0, Math.min(1, newVolume));
          playerRef.current.setVolume(clampedVolume);
        }
      };

      const handlePlaybackRateChange = (rate: number) => {
        if (videoRef.current && isFinite(rate) && rate > 0) {
          videoRef.current.playbackRate = rate;
        }
        setShowSpeedMenu(false);
      };

      const isHlsStream =
        src?.toLowerCase().includes(".m3u8") ||
        src?.toLowerCase().includes("/hls/");
      const isLiveUiMode = isLiveStream === true;
      const shouldShowEnhancedControls =
        showCustomControls &&
        (isHlsStream ? !isLiveUiMode : true);

      const criticalPropsKey = useMemo(() => {
        return CRITICAL_PROPS.map((prop) => `${prop}:${props[prop]}`).join("|");
      }, [
        src,
        allowNativeHls,
        isLiveStream,
        licenseKey,
        isVmap,
        vmapUrl,
        lowLatencyMode,
        driftToleranceMs,
        vastMode,
      ]);

      useEffect(() => {
        if (typeof window === "undefined") return;
        const el = videoRef.current;
        if (!el || !src) return;

        if (!licenseKey) {
          setShowLicenseWarning(true);
          setIsLoading(false);
          console.warn(
            "StormcloudVideoPlayer: License key is required but not provided. Please set the licenseKey prop to use the player."
          );
          return;
        }

        setShowLicenseWarning(false);

        if (debugAdTiming) {
          console.log("[StormcloudUI] Initializing player, isLoading=true");
        }

        if (playerRef.current) {
          try {
            playerRef.current.destroy();
          } catch {}
          playerRef.current = null;
        }

        const cfg: StormcloudVideoPlayerConfig = {
          src,
          videoElement: el,
        } as StormcloudVideoPlayerConfig;
        if (autoplay !== undefined) cfg.autoplay = autoplay;
        if (muted !== undefined) cfg.muted = muted;
        if (lowLatencyMode !== undefined) cfg.lowLatencyMode = lowLatencyMode;
        if (allowNativeHls !== undefined) cfg.allowNativeHls = allowNativeHls;
        if (isLiveStream !== undefined) cfg.isLiveStream = isLiveStream;
        if (driftToleranceMs !== undefined)
          cfg.driftToleranceMs = driftToleranceMs;
        if (immediateManifestAds !== undefined)
          cfg.immediateManifestAds = immediateManifestAds;
        if (debugAdTiming !== undefined) cfg.debugAdTiming = debugAdTiming;
        if (showCustomControls !== undefined)
          cfg.showCustomControls = showCustomControls;
        if (onVolumeToggle !== undefined) cfg.onVolumeToggle = onVolumeToggle;
        if (onFullscreenToggle !== undefined)
          cfg.onFullscreenToggle = onFullscreenToggle;
        if (onControlClick !== undefined) cfg.onControlClick = onControlClick;
        if (licenseKey !== undefined) cfg.licenseKey = licenseKey;
        if (vastMode !== undefined) cfg.vastMode = vastMode;
        if (vastTagUrl !== undefined) cfg.vastTagUrl = vastTagUrl;
        if (isVmap !== undefined) cfg.isVmap = isVmap;
        if (vmapUrl !== undefined) cfg.vmapUrl = vmapUrl;
        if (minSegmentsBeforePlay !== undefined)
          cfg.minSegmentsBeforePlay = minSegmentsBeforePlay;
        if (ctvAdRequest !== undefined) cfg.ctvAdRequest = ctvAdRequest;

        const player = new StormcloudVideoPlayer(cfg);
        playerRef.current = player;
        player
          .load()
          .then(() => {
            const showNative = player.shouldShowNativeControls();
            setShouldShowNativeControls(showNative);
            if (debugAdTiming) {
              console.log(
                "[StormcloudUI] Player loaded successfully, waiting for video ready"
              );
            }
            onReady?.(player);
          })
          .catch((error) => {
            console.error(
              "StormcloudVideoPlayer: Failed to load player:",
              error
            );
            setIsLoading(false);
            onReady?.(player);
          });

        return () => {
          try {
            player.destroy();
          } catch {}
          playerRef.current = null;
        };
      }, [criticalPropsKey]);

      useEffect(() => {
        if (!playerRef.current) return;

        try {
          if (autoplay !== undefined && playerRef.current.videoElement) {
            playerRef.current.videoElement.autoplay = autoplay;
          }
          if (muted !== undefined && !playerRef.current.isShowingAds()) {
            playerRef.current.setMuted(muted);
          }
        } catch (error) {
          console.warn("Failed to update player properties:", error);
        }
      }, [autoplay, muted]);

      useEffect(() => {
        if (!playerRef.current) return;

        const checkAdStatus = () => {
          if (playerRef.current) {
            const showAdsFromMethod = playerRef.current.isShowingAds();
            const showAdsFromAttribute = videoRef.current?.dataset?.stormcloudAdPlaying === "true";
            const showAds = showAdsFromMethod || showAdsFromAttribute;
            const currentIndex = playerRef.current.getCurrentAdIndex();
            const totalAds = playerRef.current.getTotalAdsInBreak();
            const remainingMs = playerRef.current.getAdRemainingMs();
            const remainingSeconds = showAds
              ? Math.max(0, Math.ceil(remainingMs / 1000))
              : 0;

            setAdStatus((prev) => {
              if (
                prev.showAds !== showAds ||
                prev.currentIndex !== currentIndex ||
                prev.totalAds !== totalAds ||
                prev.remainingSeconds !== remainingSeconds
              ) {
                if (showAds && !prev.showAds) {
                  setShowCenterPlay(false);
                }
                return { showAds, currentIndex, totalAds, remainingSeconds };
              }
              return prev;
            });
          }
        };

        const interval = setInterval(checkAdStatus, 50);
        return () => clearInterval(interval);
      }, []);

      useEffect(() => {
        if (typeof window === "undefined" || !playerRef.current) return;

        const handleResize = () => {
          if (playerRef.current && videoRef.current) {
            if (typeof playerRef.current.resize === "function") {
              playerRef.current.resize();
            }
          }
          setViewportWidth(window.innerWidth);
          setIsPortrait(window.innerHeight > window.innerWidth);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
      }, []);

      useEffect(() => {
        if (!playerRef.current || !videoRef.current) return;

        const updateStates = () => {
          if (playerRef.current && videoRef.current) {
            setIsMuted(playerRef.current.isMuted());
            setIsPlaying(!videoRef.current.paused);

            const currentTimeValue = videoRef.current.currentTime;
            setCurrentTime(isFinite(currentTimeValue) ? currentTimeValue : 0);

            const durationValue = videoRef.current.duration;
            setDuration(isFinite(durationValue) ? durationValue : 0);

            const volumeValue = playerRef.current.getVolume();
            setVolume(
              isFinite(volumeValue) ? Math.max(0, Math.min(1, volumeValue)) : 1
            );

            const rateValue = videoRef.current.playbackRate;
            setPlaybackRate(
              isFinite(rateValue) && rateValue > 0 ? rateValue : 1
            );
          }
          setIsFullscreen(
            document.fullscreenElement === wrapperRef.current
          );
        };

        const interval = setInterval(updateStates, 200);

        const handleFullscreenChange = () => {
          setIsFullscreen(
            document.fullscreenElement === wrapperRef.current
          );
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);

        return () => {
          clearInterval(interval);
          document.removeEventListener(
            "fullscreenchange",
            handleFullscreenChange
          );
        };
      }, []);

      useEffect(() => {
        if (!videoRef.current) return;

        const handleLoadedMetadata = () => {
          const video = videoRef.current;
          if (video) {
            if (video.videoWidth > 0 && video.videoHeight > 0) {
              setPlayerAspectRatio(video.videoWidth / video.videoHeight);
            }
            void video.offsetHeight;
          }
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: loadedmetadata, readyState:",
              videoRef.current?.readyState
            );
          }
        };

        const handleLoadedData = () => {
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: loadeddata, readyState:",
              videoRef.current?.readyState
            );
          }
        };

        const handleLoadStart = () => {
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: loadstart, readyState:",
              videoRef.current?.readyState
            );
          }
        };

        const handleCanPlay = () => {
          setIsLoading(false);
          if (bufferingTimeoutRef.current) {
            clearTimeout(bufferingTimeoutRef.current);
            bufferingTimeoutRef.current = null;
          }
          setIsBuffering(false);
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: canplay, readyState:",
              videoRef.current?.readyState,
              "- clearing loading state, isLoading=false"
            );
          }
        };

        const handleCanPlayThrough = () => {
          setIsLoading(false);
          if (bufferingTimeoutRef.current) {
            clearTimeout(bufferingTimeoutRef.current);
            bufferingTimeoutRef.current = null;
          }
          setIsBuffering(false);
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: canplaythrough, readyState:",
              videoRef.current?.readyState,
              "- clearing loading state, isLoading=false"
            );
          }
        };

        const handleWaiting = () => {
          if (bufferingTimeoutRef.current) {
            clearTimeout(bufferingTimeoutRef.current);
          }

          bufferingTimeoutRef.current = window.setTimeout(() => {
            setIsBuffering(true);
            if (debugAdTiming) {
              console.log(
                "[StormcloudUI] Video buffering detected (after 300ms delay), readyState:",
                videoRef.current?.readyState,
                "- showing spinner, isBuffering=true"
              );
            }
          }, 300);

          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: waiting, readyState:",
              videoRef.current?.readyState,
              "- buffering delay started (300ms)"
            );
          }
        };

        const handlePlaying = () => {
          setIsLoading(false);
          if (bufferingTimeoutRef.current) {
            clearTimeout(bufferingTimeoutRef.current);
            bufferingTimeoutRef.current = null;
          }
          setIsBuffering(false);
          setShowCenterPlay(false);
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: playing, readyState:",
              videoRef.current?.readyState,
              "- playback started, isLoading=false, isBuffering=false"
            );
          }
        };

      const handlePause = () => {
        const isAdActive = playerRef.current?.isShowingAds() || 
          videoRef.current?.dataset?.stormcloudAdPlaying === "true";
        
        if (playerRef.current && !isAdActive) {
          setShowCenterPlay(true);
        } else {
          setShowCenterPlay(false);
        }
      };

        const handleEnded = () => {
          setShowCenterPlay(true);
        };

        const video = videoRef.current;
        video.addEventListener("loadstart", handleLoadStart);
        video.addEventListener("loadedmetadata", handleLoadedMetadata);
        video.addEventListener("loadeddata", handleLoadedData);
        video.addEventListener("canplay", handleCanPlay);
        video.addEventListener("canplaythrough", handleCanPlayThrough);
        video.addEventListener("waiting", handleWaiting);
        video.addEventListener("playing", handlePlaying);
        video.addEventListener("pause", handlePause);
        video.addEventListener("ended", handleEnded);

        if (video.paused) {
          setShowCenterPlay(true);
        }

        return () => {
          if (bufferingTimeoutRef.current) {
            clearTimeout(bufferingTimeoutRef.current);
            bufferingTimeoutRef.current = null;
          }

          video.removeEventListener("loadstart", handleLoadStart);
          video.removeEventListener("loadedmetadata", handleLoadedMetadata);
          video.removeEventListener("loadeddata", handleLoadedData);
          video.removeEventListener("canplay", handleCanPlay);
          video.removeEventListener("canplaythrough", handleCanPlayThrough);
          video.removeEventListener("waiting", handleWaiting);
          video.removeEventListener("playing", handlePlaying);
          video.removeEventListener("pause", handlePause);
          video.removeEventListener("ended", handleEnded);
        };
      }, [debugAdTiming]);

      useEffect(() => {
        setPlayerAspectRatio(DEFAULT_PLAYER_ASPECT_RATIO);
      }, [src]);

      useEffect(() => {
        return () => {
          if (controlsTimerRef.current) {
            clearTimeout(controlsTimerRef.current);
          }
        };
      }, []);

      const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

      const VolumeIcon = isMuted || volume === 0
        ? FaVolumeMute
        : volume < 0.5
          ? FaVolumeDown
          : FaVolumeUp;

      return (
        <>
          <style>
            {`
              @keyframes sc-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
              @keyframes sc-loading-glow {
                0%, 100% { opacity: 0.85; transform: scale(1); }
                50% { opacity: 1; transform: scale(1.05); }
              }
              @keyframes sc-pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.6; }
              }
              @keyframes sc-fade-in {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
              }
              .sc-wrapper:fullscreen,
              .sc-wrapper:has(*:fullscreen) {
                border-radius: 0 !important;
                box-shadow: none !important;
                width: 100vw !important;
                height: 100vh !important;
                max-width: 100vw !important;
                max-height: 100vh !important;
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                z-index: 999999 !important;
                background: #000 !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
              }
              .sc-ctrl-btn {
                background: none;
                border: none;
                color: #fff;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                padding: 8px;
                transition: background 0.15s ease, opacity 0.15s ease;
                opacity: 0.9;
              }
              .sc-ctrl-btn:hover {
                opacity: 1;
                background: rgba(255, 255, 255, 0.1);
              }
              .sc-ctrl-btn:active {
                opacity: 0.7;
              }
              .sc-controls-bar {
                transition: opacity 0.35s ease, transform 0.35s ease;
              }
              .sc-progress-track:hover .sc-progress-thumb {
                transform: translate(-50%, -50%) scale(1) !important;
              }
              .sc-loading-hidden .sc-loading-indicator {
                display: none !important;
              }
            `}
          </style>
          <div
            ref={wrapperRef}
            className={`sc-wrapper ${wrapperClassName || ""}`}
            onMouseMove={handleWrapperMouseMove}
            onMouseLeave={handleWrapperMouseLeave}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: isFullscreen ? "fixed" : "relative",
              top: isFullscreen ? 0 : undefined,
              left: isFullscreen ? 0 : undefined,
              overflow: "hidden",
              width: isFullscreen ? "100vw" : "100%",
              height: isFullscreen ? "100vh" : "auto",
              minHeight: isFullscreen ? "100vh" : "auto",
              aspectRatio: isFullscreen ? undefined : playerAspectRatio,
              maxWidth: isFullscreen ? "100vw" : "100%",
              maxHeight: isFullscreen ? "100vh" : "none",
              zIndex: isFullscreen ? 999999 : undefined,
              backgroundColor: "#000",
              borderRadius: isFullscreen ? 0 : undefined,
              boxShadow: isFullscreen ? "none" : undefined,
              ...wrapperStyle,
            }}
          >
            <video
              ref={videoRef}
              className={className}
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                maxWidth: "100%",
                maxHeight: isFullscreen ? "100%" : "none",
                objectFit: isFullscreen ? "cover" : "contain",
                backgroundColor: "#000",
                aspectRatio: isFullscreen ? "unset" : undefined,
                ...style,
              }}
              controls={
                shouldShowNativeControls && controls && !showCustomControls
              }
              playsInline={playsInline}
              preload={preload}
              poster={poster}
              {...restVideoAttrs}
            >
              {children}
            </video>

            {(isLoading || isBuffering) && !hideLoadingIndicator && (
              <div
                className="sc-loading-indicator"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 20,
                  width: `${Math.max(34, 38 * responsiveScale)}px`,
                  height: `${Math.max(34, 38 * responsiveScale)}px`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: "sc-loading-glow 1.4s ease-in-out infinite",
                  filter: "drop-shadow(0 6px 14px rgba(0, 0, 0, 0.55))",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    border: "3px solid rgba(255, 255, 255, 0.25)",
                    borderTopColor: "#ff0000",
                    borderRightColor: "rgba(255, 255, 255, 0.85)",
                    animation: "sc-spin 0.8s linear infinite",
                  }}
                />
                <div
                  style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#ff0000",
                    boxShadow: "0 0 10px rgba(255, 0, 0, 0.65)",
                  }}
                />
              </div>
            )}

            {showLicenseWarning && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 25,
                  background: "rgba(220, 38, 38, 0.92)",
                  backdropFilter: "blur(24px)",
                  color: "white",
                  padding: "28px 36px",
                  borderRadius: "14px",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  boxShadow: "0 24px 64px rgba(0, 0, 0, 0.5)",
                  textAlign: "center",
                  maxWidth: "400px",
                  margin: "0 16px",
                }}
              >
                <div style={{ fontSize: "18px", fontWeight: "700", marginBottom: "10px", letterSpacing: "-0.02em" }}>
                  License Key Required
                </div>
                <div style={{ fontSize: "13px", lineHeight: "1.6", color: "rgba(255, 255, 255, 0.85)" }}>
                  Please provide a valid license key to use the Stormcloud Video
                  Player.
                  <br />
                  Contact your administrator for licensing information.
                </div>
              </div>
            )}

            {adStatus.showAds && (
              <div
                style={{
                  position: "absolute",
                  top: `${12 * responsiveScale}px`,
                  left: `${12 * responsiveScale}px`,
                  zIndex: 50,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  pointerEvents: "none",
                  animation: "sc-fade-in 0.3s ease",
                }}
              >
                <div
                  style={{
                    background: "rgba(234, 179, 8, 0.95)",
                    color: "#111",
                    padding: `${4 * responsiveScale}px ${12 * responsiveScale}px`,
                    borderRadius: "6px",
                    fontSize: `${11 * responsiveScale}px`,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase" as const,
                    boxShadow: "0 2px 12px rgba(234, 179, 8, 0.35)",
                  }}
                >
                  {`Ad ${Math.max(1, adStatus.currentIndex)}`}
                </div>
              </div>
            )}

            {showCenterPlay &&
              !isLoading &&
              !isBuffering &&
              !showLicenseWarning &&
              !adStatus.showAds && (
                <div
                  onClick={handleCenterPlayClick}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 15,
                    cursor: "pointer",
                    background: "rgba(0, 0, 0, 0.55)",
                    backdropFilter: "blur(8px)",
                    borderRadius: "50%",
                    width: `${92 * responsiveScale}px`,
                    height: `${92 * responsiveScale}px`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background 0.2s ease, transform 0.2s ease",
                    animation: "sc-fade-in 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "rgba(0, 0, 0, 0.72)";
                    (e.currentTarget as HTMLElement).style.transform = "translate(-50%, -50%) scale(1.06)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "rgba(0, 0, 0, 0.55)";
                    (e.currentTarget as HTMLElement).style.transform = "translate(-50%, -50%) scale(1)";
                  }}
                  title="Play"
                >
                  <FaPlay
                    size={Math.max(24, 32 * responsiveScale)}
                    color="white"
                    style={{ marginLeft: "4px" }}
                  />
                </div>
              )}

            {shouldShowEnhancedControls && !showLicenseWarning ? (
              <div
                className="sc-controls-bar"
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: "linear-gradient(0deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.35) 60%, transparent 100%)",
                  padding: `${20 * responsiveScale}px ${12 * responsiveScale}px ${8 * responsiveScale}px`,
                  zIndex: 10,
                  opacity: controlsVisible || adStatus.showAds ? 1 : 0,
                  transform: controlsVisible || adStatus.showAds ? "translateY(0)" : "translateY(6px)",
                  pointerEvents: controlsVisible || adStatus.showAds ? "auto" : "none",
                }}
              >
                {/* Progress Bar */}
                <div
                  className="sc-progress-track"
                  style={{
                    width: "100%",
                    height: "3px",
                    background: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "1.5px",
                    marginBottom: `${8 * responsiveScale}px`,
                    cursor: "pointer",
                    position: "relative",
                    transition: "height 0.1s ease",
                  }}
                  onClick={handleTimelineSeek}
                  onMouseEnter={(e) => { e.currentTarget.style.height = "5px"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.height = "3px"; }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0, left: 0, bottom: 0,
                      background: adStatus.showAds ? "#f5c518" : "#ff0000",
                      borderRadius: "1.5px",
                      width: `${progressPercent}%`,
                      transition: "width 0.15s linear",
                    }}
                  />
                  <div
                    className="sc-progress-thumb"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: `${progressPercent}%`,
                      transform: "translate(-50%, -50%) scale(0)",
                      width: "13px",
                      height: "13px",
                      background: adStatus.showAds ? "#f5c518" : "#ff0000",
                      borderRadius: "50%",
                      boxShadow: "0 0 4px rgba(0, 0, 0, 0.3)",
                      transition: "transform 0.1s ease, left 0.15s linear",
                    }}
                  />
                </div>

                {/* Controls Row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    color: "white",
                    gap: `${8 * responsiveScale}px`,
                  }}
                >
                  {/* Left Group */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: `${8 * responsiveScale}px`,
                    }}
                  >
                    {/* Play/Pause */}
                    <button
                      className="sc-ctrl-btn"
                      onClick={handlePlayPause}
                      style={{
                        padding: `${8 * responsiveScale}px`,
                        borderRadius: "50%",
                        minWidth: `${36 * responsiveScale}px`,
                        minHeight: `${36 * responsiveScale}px`,
                      }}
                      title={isPlaying ? "Pause" : "Play"}
                    >
                      {isPlaying ? (
                        <FaPause size={Math.max(14, 18 * responsiveScale)} />
                      ) : (
                        <FaPlay size={Math.max(14, 18 * responsiveScale)} style={{ marginLeft: "2px" }} />
                      )}
                    </button>

                    {/* Volume Group - YouTube Style */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        paddingRight: `${6 * responsiveScale}px`,
                      }}
                      onMouseEnter={() => setShowVolumeSlider(true)}
                      onMouseLeave={() => setShowVolumeSlider(false)}
                    >
                      <button
                        className="sc-ctrl-btn"
                        onClick={() => {
                          if (playerRef.current) playerRef.current.toggleMute();
                          onVolumeToggle?.();
                          resetControlsTimer();
                        }}
                        style={{
                          padding: `${8 * responsiveScale}px`,
                          borderRadius: "50%",
                          minWidth: `${36 * responsiveScale}px`,
                          minHeight: `${36 * responsiveScale}px`,
                        }}
                        title={isMuted ? "Unmute" : "Mute"}
                      >
                        <VolumeIcon size={Math.max(14, 18 * responsiveScale)} />
                      </button>
                      <div
                        style={{
                          width: showVolumeSlider ? `${68 * responsiveScale}px` : "0px",
                          overflow: "hidden",
                          transition: "width 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          display: "flex",
                          alignItems: "center",
                          paddingLeft: showVolumeSlider ? `${3 * responsiveScale}px` : "0",
                          paddingRight: showVolumeSlider ? `${8 * responsiveScale}px` : "0",
                        }}
                      >
                        <div
                          style={{
                            position: "relative",
                            width: `${56 * responsiveScale}px`,
                            height: "3px",
                            cursor: "pointer",
                            borderRadius: "1.5px",
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const el = e.currentTarget;
                            const move = (ev: MouseEvent) => {
                              const r = el.getBoundingClientRect();
                              handleVolumeChange(Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width)));
                            };
                            const up = () => { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); };
                            document.addEventListener("mousemove", move);
                            document.addEventListener("mouseup", up);
                            const r = el.getBoundingClientRect();
                            handleVolumeChange(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const r = e.currentTarget.getBoundingClientRect();
                            handleVolumeChange(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
                          }}
                        >
                          <div style={{ position: "absolute", inset: 0, background: "rgba(255, 255, 255, 0.2)", borderRadius: "1.5px" }} />
                          <div
                            style={{
                              position: "absolute",
                              top: 0, left: 0, bottom: 0,
                              width: `${(isMuted ? 0 : volume) * 100}%`,
                              background: "#fff",
                              borderRadius: "1.5px",
                              transition: "width 0.1s ease-out",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: `${(isMuted ? 0 : volume) * 100}%`,
                              transform: "translate(-50%, -50%)",
                              width: "12px",
                              height: "12px",
                              background: "#fff",
                              borderRadius: "50%",
                              boxShadow: "0 0 3px rgba(0, 0, 0, 0.3)",
                              transition: "left 0.1s ease-out",
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Time Display */}
                    <div
                      style={{
                        fontSize: `${13 * responsiveScale}px`,
                        fontFamily: "Roboto, 'Segoe UI', Arial, sans-serif",
                        color: "rgba(255, 255, 255, 0.9)",
                        display: viewportWidth < 480 ? "none" : "block",
                        fontVariantNumeric: "tabular-nums",
                        userSelect: "none",
                        marginLeft: `${4 * responsiveScale}px`,
                      }}
                    >
                      {formatTime(currentTime)}{" "}
                      <span style={{ color: "rgba(255,255,255,0.5)" }}>/</span>{" "}
                      {formatTime(duration)}
                    </div>
                  </div>

                  {/* Right Group */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: `${8 * responsiveScale}px`,
                    }}
                  >
                    {/* Speed Selector */}
                    <div style={{ position: "relative", display: viewportWidth < 600 ? "none" : "block" }}>
                      <button
                        className="sc-ctrl-btn"
                        onClick={() => { setShowSpeedMenu(!showSpeedMenu); resetControlsTimer(); }}
                        style={{
                          padding: `${6 * responsiveScale}px ${10 * responsiveScale}px`,
                          borderRadius: `${6 * responsiveScale}px`,
                          fontSize: `${13 * responsiveScale}px`,
                          fontFamily: "'SF Mono', 'Cascadia Code', monospace",
                          fontWeight: "600",
                          minHeight: `${32 * responsiveScale}px`,
                        }}
                        title="Playback Speed"
                      >
                        {playbackRate}x
                      </button>

                      {showSpeedMenu && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: "100%",
                            right: 0,
                            marginBottom: "8px",
                            background: "rgba(0, 0, 0, 0.8)",
                            backdropFilter: "blur(24px)",
                            WebkitBackdropFilter: "blur(24px)",
                            borderRadius: "10px",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            overflow: "hidden",
                            minWidth: "80px",
                            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.5)",
                            animation: "sc-fade-in 0.15s ease",
                          }}
                        >
                          {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((speed) => (
                            <button
                              key={speed}
                              onClick={() => handlePlaybackRateChange(speed)}
                              style={{
                                display: "block",
                                width: "100%",
                                padding: `${8 * responsiveScale}px ${14 * responsiveScale}px`,
                                background: playbackRate === speed
                                  ? "rgba(255, 255, 255, 0.12)"
                                  : "transparent",
                                border: "none",
                                color: playbackRate === speed ? "#fff" : "rgba(255, 255, 255, 0.7)",
                                cursor: "pointer",
                                fontSize: `${12 * responsiveScale}px`,
                                fontFamily: "'SF Mono', monospace",
                                fontWeight: playbackRate === speed ? "700" : "500",
                                textAlign: "center",
                                transition: "background 0.15s ease, color 0.15s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (playbackRate !== speed) {
                                  (e.target as HTMLElement).style.background = "rgba(255, 255, 255, 0.08)";
                                  (e.target as HTMLElement).style.color = "#fff";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (playbackRate !== speed) {
                                  (e.target as HTMLElement).style.background = "transparent";
                                  (e.target as HTMLElement).style.color = "rgba(255, 255, 255, 0.7)";
                                }
                              }}
                            >
                              {speed}x
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Fullscreen */}
                    <button
                      className="sc-ctrl-btn"
                      onClick={() => {
                        if (onFullscreenToggle) {
                          onFullscreenToggle();
                        } else if (wrapperRef.current) {
                          if (!document.fullscreenElement) {
                            wrapperRef.current.requestFullscreen().catch(() => {});
                          } else {
                            document.exitFullscreen().catch(() => {});
                          }
                        }
                        resetControlsTimer();
                      }}
                      style={{
                        padding: `${8 * responsiveScale}px`,
                        borderRadius: "50%",
                        minWidth: `${36 * responsiveScale}px`,
                        minHeight: `${36 * responsiveScale}px`,
                      }}
                      title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                    >
                      {isFullscreen ? (
                        <CompressIcon size={Math.max(14, 18 * responsiveScale)} />
                      ) : (
                        <ExpandIcon size={Math.max(14, 18 * responsiveScale)} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              showCustomControls && !showLicenseWarning && (
                <>
                  <div
                    style={{
                      position: "absolute",
                      top: `${10 * responsiveScale}px`,
                      left: `${10 * responsiveScale}px`,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      zIndex: 10,
                      opacity: controlsVisible ? 1 : 0,
                      transition: "opacity 0.35s ease",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#ff3b30",
                        animation: "sc-pulse 1.5s ease-in-out infinite",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: `${12 * responsiveScale}px`,
                        fontFamily: "'SF Pro Display', 'Segoe UI', Arial, sans-serif",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        color: "#fff",
                        textShadow: "0 1px 3px rgba(0,0,0,0.6)",
                        userSelect: "none",
                      }}
                    >
                      LIVE
                    </span>
                  </div>

                  <div
                  className="sc-controls-bar"
                  style={{
                    position: "absolute",
                    bottom: `${10 * responsiveScale}px`,
                    right: `${10 * responsiveScale}px`,
                    display: "flex",
                    flexDirection: isPortrait ? "column" : "row",
                    alignItems: isPortrait ? "flex-end" : "center",
                    gap: `${8 * responsiveScale}px`,
                    zIndex: 10,
                    opacity: controlsVisible ? 1 : 0,
                    transform: controlsVisible ? "translateY(0)" : "translateY(4px)",
                    pointerEvents: controlsVisible ? "auto" : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "rgba(0, 0, 0, 0.6)",
                      borderRadius: `${18 * responsiveScale}px`,
                      padding: "2px",
                      paddingRight: `${8 * responsiveScale}px`,
                    }}
                    onMouseEnter={() => setShowVolumeSlider(true)}
                    onMouseLeave={() => setShowVolumeSlider(false)}
                  >
                    <button
                      className="sc-ctrl-btn"
                      onClick={() => {
                        if (playerRef.current) playerRef.current.toggleMute();
                        onVolumeToggle?.();
                        resetControlsTimer();
                      }}
                      style={{
                        padding: `${8 * responsiveScale}px`,
                        borderRadius: "50%",
                        minWidth: `${36 * responsiveScale}px`,
                        minHeight: `${36 * responsiveScale}px`,
                      }}
                      title={isMuted ? "Unmute" : "Mute"}
                    >
                      <VolumeIcon size={Math.max(14, 18 * responsiveScale)} />
                    </button>
                    <div
                      style={{
                        width: showVolumeSlider ? `${68 * responsiveScale}px` : "0px",
                        overflow: "hidden",
                        transition: "width 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: showVolumeSlider ? `${3 * responsiveScale}px` : "0",
                        paddingRight: showVolumeSlider ? `${10 * responsiveScale}px` : "0",
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          width: `${56 * responsiveScale}px`,
                          height: "3px",
                          cursor: "pointer",
                          borderRadius: "1.5px",
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const el = e.currentTarget;
                          const move = (ev: MouseEvent) => {
                            const r = el.getBoundingClientRect();
                            handleVolumeChange(Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width)));
                          };
                          const up = () => { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); };
                          document.addEventListener("mousemove", move);
                          document.addEventListener("mouseup", up);
                          const r = el.getBoundingClientRect();
                          handleVolumeChange(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const r = e.currentTarget.getBoundingClientRect();
                          handleVolumeChange(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
                        }}
                      >
                        <div style={{ position: "absolute", inset: 0, background: "rgba(255, 255, 255, 0.2)", borderRadius: "1.5px" }} />
                        <div
                          style={{
                            position: "absolute",
                            top: 0, left: 0, bottom: 0,
                            width: `${(isMuted ? 0 : volume) * 100}%`,
                            background: "#fff",
                            borderRadius: "1.5px",
                            transition: "width 0.1s ease-out",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: `${(isMuted ? 0 : volume) * 100}%`,
                            transform: "translate(-50%, -50%)",
                            width: "12px",
                            height: "12px",
                            background: "#fff",
                            borderRadius: "50%",
                            boxShadow: "0 0 3px rgba(0, 0, 0, 0.3)",
                            transition: "left 0.1s ease-out",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    className="sc-ctrl-btn"
                    onClick={() => {
                      if (onFullscreenToggle) {
                        onFullscreenToggle();
                      } else if (wrapperRef.current) {
                        if (!document.fullscreenElement) {
                          wrapperRef.current.requestFullscreen().catch(() => {});
                        } else {
                          document.exitFullscreen().catch(() => {});
                        }
                      }
                      resetControlsTimer();
                    }}
                    style={{
                      padding: `${8 * responsiveScale}px`,
                      borderRadius: "50%",
                      minWidth: `${36 * responsiveScale}px`,
                      minHeight: `${36 * responsiveScale}px`,
                      background: "rgba(0, 0, 0, 0.6)",
                    }}
                    title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                  >
                    {isFullscreen ? (
                      <CompressIcon size={Math.max(14, 18 * responsiveScale)} />
                    ) : (
                      <ExpandIcon size={Math.max(14, 18 * responsiveScale)} />
                    )}
                  </button>
                  </div>
                </>
              )
            )}

            {onControlClick && (
              <div
                onClick={onControlClick}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1,
                  cursor: "pointer",
                }}
              />
            )}
          </div>
        </>
      );
    },
    (prevProps, nextProps) => {
      for (const prop of CRITICAL_PROPS) {
        if ((prevProps as any)[prop] !== (nextProps as any)[prop]) {
          return false;
        }
      }

      const uiProps = [
        "autoplay",
        "muted",
        "controls",
        "showCustomControls",
        "className",
        "style",
        "wrapperClassName",
        "wrapperStyle",
        "playsInline",
        "preload",
        "poster",
        "children",
      ] as const;

      for (const prop of uiProps) {
        if ((prevProps as any)[prop] !== (nextProps as any)[prop]) {
          return false;
        }
      }

      const callbackProps = [
        "onReady",
        "onVolumeToggle",
        "onFullscreenToggle",
        "onControlClick",
      ] as const;
      for (const prop of callbackProps) {
        if ((prevProps as any)[prop] !== (nextProps as any)[prop]) {
          return false;
        }
      }

      return true;
    }
  );
